import TextField from '@app/src/components/Ui/TextField'
import RequestError from '@app/src/errors/RequestError'
import useErrorNotification from '@app/src/hooks/errorNotification'
import { geocode, suggest } from '@app/src/utils/ArcGisApi'
import { Autocomplete, MenuItem, MenuList, Paper, Popper, Typography } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import type { DeepMap, FieldError, FieldValues } from 'react-hook-form'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useDebouncedCallback } from 'use-debounce'

interface LocationSuggestion {
  magicKey: string
  text: string
}

export interface SearchResult {
  candidates: {
    location: {
      x: number
      y: number
    }
    attributes: {
      LongLabel: string
      Country: string
      Region: string
      Subregion: string
      City: string
      District: string
    }
  }[]
}

interface ControlledLocationFieldProps {
  addressName: string
  locationName: string
  valueSetter: (searchResult: SearchResult) => void
  fieldErrors?: DeepMap<FieldValues, FieldError>
  rules?: { [key: string]: string }
  label?: string
  onApiError?: () => void
  hideHelperText?: boolean
  helperText?: string
  manualFallback?: {
    label: string
    onClick: () => void
  }
}

const ControlledLocationField: React.FC<ControlledLocationFieldProps> = ({
  label,
  rules,
  addressName,
  locationName,
  valueSetter,
  fieldErrors,
  onApiError,
  hideHelperText,
  helperText,
  manualFallback,
}) => {
  const [searchResults, setSearchResults] = useState<LocationSuggestion[]>([])
  const [search, setSearch] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false)
  const anchorRef = useRef<HTMLDivElement | null>(null)
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestIdRef = useRef(0)
  const { showErrorNotification } = useErrorNotification()
  const { formatMessage } = useIntl()
  const { control, setValue, clearErrors, watch } = useFormContext()

  const locationWatch = watch(locationName)

  const fetchAddressSuggestions = useDebouncedCallback(async () => {
    const requestId = ++requestIdRef.current
    try {
      const result = await suggest(search)
      if (requestIdRef.current !== requestId) return
      setSearchResults(result?.suggestions?.length ? result.suggestions : [])
    } catch {
      if (requestIdRef.current !== requestId) return
      onApiError?.()
      setSearchResults([])
    } finally {
      if (requestIdRef.current === requestId) setIsLoadingSuggestions(false)
    }
  }, 200)

  useEffect(
    () => () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
      fetchAddressSuggestions.cancel()
      requestIdRef.current += 1
    },
    [fetchAddressSuggestions],
  )

  const fetchSelectedLocation = async (
    event: React.SyntheticEvent<Element, Event>,
    value: string | LocationSuggestion | null,
  ) => {
    clearErrors(addressName)
    if (value === null || value === '') {
      setValue(locationName, undefined)
      setValue(addressName, undefined)
      setSearch('')
      return
    }
    if (typeof value === 'string') {
      return
    }

    try {
      const result = await geocode(value.magicKey)

      // If the location search fails we set the address to the input value
      if (result.error) {
        showErrorNotification({ requestError: result.error })
        onApiError?.()
        return
      }
      valueSetter(result)
    } catch (error) {
      onApiError?.()
      const requestError = error instanceof RequestError ? error : null
      showErrorNotification({ requestError })
    }
  }

  function onSearchChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const wasGeocoded = locationWatch?.lat != null || locationWatch?.lng != null
    if (wasGeocoded) {
      setValue(locationName, undefined)
    }
    setSearch(event.target.value)
  }

  useEffect(() => {
    if (search.length) {
      setIsLoadingSuggestions(true)
      fetchAddressSuggestions()
    } else {
      requestIdRef.current += 1
      setSearchResults([])
      setIsLoadingSuggestions(false)
    }
  }, [search])

  const shouldShowManualFallback = Boolean(
    manualFallback && isFocused && search.length > 0 && !isLoadingSuggestions && searchResults.length === 0,
  )

  return (
    <>
      <Controller
        control={control}
        name={addressName}
        rules={rules}
        render={({ onChange, value }) => (
          <div ref={anchorRef}>
            <Autocomplete
              freeSolo
              options={searchResults}
              getOptionLabel={option => (typeof option === 'string' ? option : option.text || option.address)}
              noOptionsText={formatMessage({ id: 'general.typeToSearch' })}
              onChange={fetchSelectedLocation}
              filterOptions={options => options}
              isOptionEqualToValue={(option, value) => option.text === value.address}
              value={value || ''}
              renderInput={params => (
                <TextField
                  hoveringLabel
                  placeholder={formatMessage({ id: 'claim.createClaim.orgCreateStep.typeAddress' })}
                  label={label}
                  type="text"
                  error={Boolean(fieldErrors)}
                  helperText={hideHelperText ? undefined : fieldErrors?.message || helperText}
                  onChange={e => {
                    onSearchChange(e)
                    onChange(e)
                  }}
                  onFocus={() => {
                    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
                    setIsFocused(true)
                  }}
                  onBlur={() => {
                    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
                    blurTimeoutRef.current = setTimeout(() => setIsFocused(false), 150)
                  }}
                  {...params}
                />
              )}
            />
          </div>
        )}
      />
      {manualFallback && (
        <Popper
          open={shouldShowManualFallback}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
        >
          <Paper elevation={1} sx={{ mt: -2, borderRadius: 2 }}>
            <MenuList dense>
              <MenuItem
                onMouseDown={e => {
                  e.preventDefault()
                  manualFallback.onClick()
                }}
              >
                <Typography variant="body2" color="primary" sx={{ fontStyle: 'italic' }}>
                  {manualFallback.label}
                </Typography>
              </MenuItem>
            </MenuList>
          </Paper>
        </Popper>
      )}
    </>
  )
}

export default ControlledLocationField
